import { createContext, useContext, useEffect, useState } from "react";
import enTranslations from "@/i18n/en.json";
import ptTranslations from "@/i18n/pt.json";
import esTranslations from "@/i18n/es.json";

export type Language = "en" | "pt" | "es";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const translations: Record<Language, Record<string, string>> = {
  en: enTranslations as Record<string, string>,
  pt: ptTranslations as Record<string, string>,
  es: esTranslations as Record<string, string>,
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const isLanguage = (v: unknown): v is Language => v === "en" || v === "pt" || v === "es";

function detectBrowserLanguage(): Language {
  if (typeof window === "undefined") return "en";
  const browserLang = (navigator.language || (navigator as any).userLanguage || "en").toLowerCase();
  if (browserLang.startsWith("pt")) return "pt";
  if (browserLang.startsWith("es")) return "es";
  return "en";
}

// ?lang=pt|en|es in the address picks the language (links from ads and other sites); the choice is remembered.
function languageFromUrl(): Language | null {
  try {
    const v = new URLSearchParams(window.location.search).get("lang");
    return isLanguage(v) ? v : null;
  } catch {
    return null;
  }
}

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguage] = useState<Language>(() => {
    if (typeof window !== "undefined") {
      const fromUrl = languageFromUrl();
      if (fromUrl) return fromUrl;
      let stored: string | null = null;
      try { stored = localStorage.getItem("language"); } catch { /* storage may be unavailable */ }
      if (isLanguage(stored)) return stored;
      return detectBrowserLanguage();
    }
    return "en";
  });

  useEffect(() => {
    try { localStorage.setItem("language", language); } catch { /* storage may be unavailable */ }
    document.documentElement.lang = language === "pt" ? "pt-BR" : language;
  }, [language]);

  const t = (key: string): string => {
    return translations[language][key] || translations.en[key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
}

/** True when the site is in Spanish (for components that receive `pt` as a prop). */
export function useIsEs() {
  return useLanguage().language === "es";
}
